<template>
  <div v-if="isReady">
    <!-- Photo display -->
    <div v-if="photoFile">
      <img 
        :src="photoFile" 
        class="m-auto mb-4"
      />
    </div>

    <audio 
      v-if="mediaFile && mimeType"
      ref="audioRef"
      hidden
      class="m-auto w-full md:w-[300px]"
      @play="handlePlay"
      @pause="handlePause"
      @timeupdate="handleTimeUpdate"
      @seeking="handleSeeking"
      @seeked="handleSeeked"
      @loadedmetadata="onLoadedMetadata"
      @ended="handlePause"
    >
      <source :src="mediaFile" :type="mimeType" />
    </audio>

    <div v-if="mediaFile && mimeType" class="flex flex-col w-full items-center my-5">
      <div class="flex flex-col w-full max-w-md gap-3">
        <div class="flex items-end gap-2">
          <button 
            @click="togglePlayPause"
            class="flex-shrink-0 w-14 h-14 flex items-center justify-center"
            :disabled="!mediaFile"
          >
            <PlayCircleIcon v-if="!isPlaying" class="h-full w-full " :style="{ color: tourSettings?.theme_color ?? '#000' }"  />
            
            <PauseCircleIcon v-else class="h-full w-full" :style="{ color: tourSettings?.theme_color ?? '#000' }"  /> 
          </button>

          <svg class="w-4 h-4 fill-gray-400 self-end mb-1.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 115.34 122.88"><title>headphone</title><path d="M97.74,119.52a2,2,0,0,1-.68-.12V65.93a2,2,0,0,1,.68-.12c3.34,0,6.43,5,8.48,7l-.46-12.12c0-12.8-3.72-23.11-9.68-30.93a7.37,7.37,0,0,1-5.86-2.23l-.68-.7C80.91,18.29,69,14.44,57.24,14.66S33.76,19.19,25.69,26.91l-.66.64a7.3,7.3,0,0,1-6.49,2.05c-6,7.84-9.75,18.21-9.75,31.14L8.33,74.63c2.25-2.71,6.34-8.66,10-8.82v53.71C10.52,119.36,4.91,107.05,3,101H0V60.74A57.33,57.33,0,0,1,12.83,24.57a7.35,7.35,0,0,1,1.8-7.36l1-.95C26.36,6,41.62.3,57,0s31.25,4.9,42.87,16.46l.91.93a7.33,7.33,0,0,1,1.74,7.2,57.34,57.34,0,0,1,12.81,36.13V101h-2.21c-1.89,6.14-7.54,18.57-15.39,18.57Zm-5.55,3.36h-9a3.48,3.48,0,0,1-3.48-3.47V65.65a3.49,3.49,0,0,1,3.48-3.48h9v60.71Zm-69-60.71H33a3.5,3.5,0,0,1,3.48,3.48v53.76A3.49,3.49,0,0,1,33,122.88H23.14V62.17Z"/></svg>
          <span class="text-sm text-gray-400 uppercase font-bold">audio</span>
        </div>

        <div class="flex items-center gap-3">
          <div class="flex-shrink-0 text-sm text-gray-600 min-w-[30px] text-right">
            {{ formatTime(currentTime) }}
          </div>

          <div class="flex-1">
            <div 
              class="progress bg-gray-200 rounded-full h-2 cursor-pointer" 
              @click="handleProgressClick"
              @mousedown="handleMouseDown"
              @mousemove="handleMouseMove"
              @mouseup="handleMouseUp"
              @mouseleave="handleMouseLeave"
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
            >
              <div 
                class="progress-done h-2 rounded-full transition-all duration-100"
                :style="{ width: `${progressPercentage}%`, backgroundColor: tourSettings?.theme_color ?? '#000' }"
              />
            </div>
          </div>

          <div class="flex-shrink-0 text-sm text-gray-600 min-w-[30px] text-right">
            {{ formatTime(duration) }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="block.data?.title?.[currentLocale]" class="hidden text-center text-sm">
      {{ block.data.title[currentLocale] }}
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { PlayCircleIcon, PauseCircleIcon } from '@heroicons/vue/24/solid'

export default {
  name: 'AudioBlock',
  components: {
    PlayCircleIcon,
    PauseCircleIcon
  },
  props: {
    block: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      mediaFile: null,
      mimeType: null,
      photoFile: null,
      currentTime: 0,
      duration: 0,
      seekStartTime: null,
      playStartTime: null,
      totalPlayedTime: 0,
      seekDebounceTimeout: null,
      isPlaying: false,
      isDragging: false
    }
  },
  computed: {
    progressPercentage() {
      if (this.duration === 0) return 0
      return (this.currentTime / this.duration) * 100
    },
    currentLocale() {
      // Only return locale if i18n is available
      return this.$i18n?.locale || null
    },
    isReady() {
      // Component is ready when i18n and settings are loaded
      return this.currentLocale && this.tourSettings
    },
    ...mapGetters('settings', ['tourSettings']),  
  },
  watch: {
    block: {
      handler(newBlock) {
        // Only process data when component is ready
        if (!this.isReady) return
        
        // Handle photo
        if (newBlock?.data?.photo) {
          this.photoFile = newBlock.data.photo.preview_url
        }
        
        // Handle media
        if (newBlock?.data?.media && newBlock.data.media[this.currentLocale]) {
          const mediaData = newBlock.data.media[this.currentLocale]
          this.mediaFile = mediaData.preview_url
          
          // Set mime type
          if (mediaData.mime_type?.name) {
            this.mimeType = 'audio/' + mediaData.mime_type.name
          }
        }
      },
      immediate: true
    }
  },
  beforeUnmount() {
    // Clean up timeout when component is destroyed
    if (this.seekDebounceTimeout) {
      clearTimeout(this.seekDebounceTimeout)
    }
  },
  methods: {
    togglePlayPause() {
      if (this.isPlaying) {
        this.handlePause()
      } else {
        this.handlePlay()
      }
    },

    handlePlay() {
      if (this.$refs.audioRef) {
        this.playStartTime = Date.now()
        this.isPlaying = true
        this.$refs.audioRef.play();

        const audioName = this.block.data?.media?.[this.currentLocale]?.name || 'Untitled'
        console.log(`Audio played: "${audioName}" at ${this.formatTime(this.currentTime)}`)

        // Matomo tracking
        if (window._paq) {
          window._paq.push(['trackEvent', 'Audio', 'Play', audioName, this.currentTime])
        }
      }
    },

    handlePause() {
      if (this.$refs.audioRef) {
        this.isPlaying = false
        this.$refs.audioRef.pause();

        if (this.playStartTime) {
          const sessionPlayTime = Math.floor((Date.now() - this.playStartTime) / 1000)
          const newTotalPlayedTime = this.totalPlayedTime + sessionPlayTime
          this.totalPlayedTime = newTotalPlayedTime

          const audioName = this.block.data?.media?.[this.currentLocale]?.name || 'Untitled'
          console.log(`Audio paused: "${audioName}" at ${this.formatTime(this.currentTime)}, session played time: ${this.formatTime(sessionPlayTime)}, total played time: ${this.formatTime(newTotalPlayedTime)}`)
          this.playStartTime = null

          // Matomo tracking with custom dimensions
          if (window._paq) {
            window._paq.push(['setCustomDimension', 1, this.currentTime.toString()])
            window._paq.push(['setCustomDimension', 2, sessionPlayTime.toString()])
            window._paq.push(['setCustomDimension', 3, newTotalPlayedTime.toString()])
            window._paq.push(['trackEvent', 'Audio', 'Pause', audioName, this.currentTime])
          }
        }
      }
    },

    handleTimeUpdate() {
      if (this.$refs.audioRef) {
        const newTime = this.$refs.audioRef.currentTime
        const duration = parseFloat(newTime) - this.currentTime
        console.log('Time update event fired:', newTime, 'Previous currentTime:', this.currentTime, 'Duration:', this.$refs.audioRef.duration, 'Duration:', duration)
        
        // Force the update by creating a new reference
        this.currentTime = parseFloat(newTime)
        
        console.log('Updated currentTime to:', this.currentTime)
      }
    },

    handleSeeking() {
      // If we're currently playing, we need to account for the time played so far
      if (this.playStartTime) {
        const sessionPlayTime = Math.floor((Date.now() - this.playStartTime) / 1000)
        this.totalPlayedTime = this.totalPlayedTime + sessionPlayTime
        this.playStartTime = null // Reset play start time as we're seeking
      }
      
      // Only set the seek start time once at the beginning of a seek operation
      if (this.seekStartTime === null) {
        this.seekStartTime = this.currentTime
        
        // Clear any existing timeout to prevent multiple seek end events
        if (this.seekDebounceTimeout) {
          clearTimeout(this.seekDebounceTimeout)
          this.seekDebounceTimeout = null
        }
      }
    },

    handleSeeked() {
      // Use debouncing to only track the final seek position
      if (this.seekStartTime !== null) {
        // Clear any existing timeout
        if (this.seekDebounceTimeout) {
          clearTimeout(this.seekDebounceTimeout)
        }
        
        // Set a new timeout to track the seek after a short delay
        this.seekDebounceTimeout = setTimeout(() => {
          const audioName = this.block.data?.media?.[this.currentLocale]?.name || 'Untitled'
          const fromTime = this.seekStartTime  // Raw seconds
          const toTime = this.$refs.audioRef.currentTime  // Raw seconds
          console.log(`Audio seeked: from ${this.formatTime(fromTime)} to ${this.formatTime(toTime)}`)
          
          // Matomo tracking with raw seconds values
          if (window._paq) {
            window._paq.push(['trackEvent', 'Audio', 'Seek', audioName, toTime])
            window._paq.push(['setCustomDimension', 1, fromTime])  // Raw seconds for starting position
            window._paq.push(['setCustomDimension', 2, toTime])    // Raw seconds for ending position
          }
          
          this.seekStartTime = null
          this.seekDebounceTimeout = null
          
          // If the audio is still playing after seeking, restart the play timer
          if (!this.$refs.audioRef.paused) {
            this.playStartTime = Date.now()
          }
        }, 500) // 500ms debounce time
      }
    },


    handleProgressClick(event) {
      const progressBar = event.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const progressBarWidth = rect.width
      const clickPercentage = clickX / progressBarWidth
      const newTime = clickPercentage * this.duration
      
      // Set the new time
      this.$refs.audioRef.currentTime = newTime
      this.currentTime = newTime
      
      // Trigger seeking events for tracking
      this.handleSeeking()
      this.handleSeeked()
    },

    handleMouseDown(event) {
      this.isDragging = true
      this.handleDrag(event)
      // Prevent text selection during drag
      event.preventDefault()
    },

    handleMouseMove(event) {
      if (this.isDragging) {
        this.handleDrag(event)
      }
    },

    handleMouseUp(event) {
      if (this.isDragging) {
        this.isDragging = false
        this.handleDrag(event)
        // Trigger seeking events for tracking
        this.handleSeeking()
        this.handleSeeked()
      }
    },

    handleMouseLeave(event) {
      if (this.isDragging) {
        this.isDragging = false
        this.handleDrag(event)
        // Trigger seeking events for tracking
        this.handleSeeking()
        this.handleSeeked()
      }
    },

    handleTouchStart(event) {
      this.isDragging = true
      this.handleTouchDrag(event)
      event.preventDefault()
    },

    handleTouchMove(event) {
      if (this.isDragging) {
        this.handleTouchDrag(event)
        event.preventDefault()
      }
    },

    handleTouchEnd() {
      if (this.isDragging) {
        this.isDragging = false
        // Don't call handleTouchDrag on touch end since touches array is empty
        // Just trigger the seeking events for tracking
        this.handleSeeking()
        this.handleSeeked()
      }
    },

    handleDrag(event) {
      const progressBar = event.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const progressBarWidth = rect.width
      const clickPercentage = Math.max(0, Math.min(1, clickX / progressBarWidth))
      const newTime = clickPercentage * this.duration
      
      // Update the current time during drag
      this.currentTime = newTime
      this.$refs.audioRef.currentTime = newTime
    },

    handleTouchDrag(event) {
      if (!event.touches || event.touches.length === 0) {
        return // No touches available
      }
      
      const progressBar = event.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const touch = event.touches[0]
      const clickX = touch.clientX - rect.left
      const progressBarWidth = rect.width
      const clickPercentage = Math.max(0, Math.min(1, clickX / progressBarWidth))
      const newTime = clickPercentage * this.duration
      
      // Update the current time during drag
      this.currentTime = newTime
      this.$refs.audioRef.currentTime = newTime
    },

    onLoadedMetadata(event) {
      this.duration = event.target.duration
    },

    // Helper function to format time in MM:SS format
    formatTime(timeInSeconds) {
      if (!timeInSeconds || isNaN(timeInSeconds)) return '0:00'
      const minutes = Math.floor(timeInSeconds / 60)
      const seconds = Math.floor(timeInSeconds % 60)
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.progress {
  position: relative;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.progress-done {
  position: relative;
  transition: width 0.1s ease;
}

.progress:hover {
  cursor: grab;
}

.progress:active {
  cursor: grabbing;
}
</style>


